import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Search, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '../components/SEOHead';

const NotFoundPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <SEOHead title={t('notFound.title')} />
      <div className="max-w-2xl w-full text-center">
        {/* 404 Illustration */}
        <div className="mb-8 relative">
          <div className="text-[180px] font-bold text-gray-100 leading-none select-none">
            404
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-[#be1f1a] rounded-full p-8 shadow-lg animate-bounce">
              <Search className="h-16 w-16 text-white" />
            </div>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {t('notFound.heading')}
        </h1>
        
        {/* Description */}
        <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
          {t('notFound.description')}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Link to="/">
            <Button 
              size="lg" 
              className="bg-[#be1f1a] hover:bg-[#a71b17] text-white px-8 py-6 text-base"
            >
              <Home className="h-5 w-5 mr-2" />
              {t('notFound.backHome')}
            </Button>
          </Link>
          
          <Link to="/shop">
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-[#be1f1a] text-[#be1f1a] hover:bg-[#be1f1a] hover:text-white px-8 py-6 text-base"
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              {t('notFound.browseShop')}
            </Button>
          </Link>
        </div>

        {/* Quick Links */}
        <div className="border-t border-gray-200 pt-8">
          <p className="text-sm text-gray-500 mb-4">
            {t('notFound.popularPages')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/shop" 
              className="text-sm text-gray-600 hover:text-[#be1f1a] transition-colors"
            >
              {t('notFound.shopAll')}
            </Link>
            <span className="text-gray-300">|</span>
            <Link 
              to="/search" 
              className="text-sm text-gray-600 hover:text-[#be1f1a] transition-colors"
            >
              {t('common.search')}
            </Link>
            <span className="text-gray-300">|</span>
            <Link 
              to="/account" 
              className="text-sm text-gray-600 hover:text-[#be1f1a] transition-colors"
            >
              {t('notFound.myAccount')}
            </Link>
            <span className="text-gray-300">|</span>
            <Link
              to="/page/contact-us"
              className="text-sm text-gray-600 hover:text-[#be1f1a] transition-colors"
            >
              {t('contact.title')}
            </Link>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-[#be1f1a] rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-[#F4A261] rounded-full opacity-10 blur-3xl"></div>
      </div>
    </div>
  );
};

export default NotFoundPage;
